// LiveMinimap — a small always-on top-down mini-map anchored bottom-right.
// Shows the three campus zones (Central Quad, West-Wing Baseball, South-East
// Sports Arena) as coloured icons plus a live "You Are Here" dot that
// tracks the visitor's real-time camera position by polling
// `arenaApi.playerPosRef` every 250ms.
//
// The map coordinates are hand-tuned to keep the three zones roughly to
// scale within a 160×160 SVG viewBox. World → map projection is affine:
//
//   mapX = 80 + worldX * SCALE
//   mapY = 80 + worldZ * SCALE
//
// where +worldX draws east (right on the map) and +worldZ draws south
// (down on the map). SCALE is picked so the extreme campus coords —
// Baseball at x=-45 and Football at z=70 — land at ~10px inside the map
// edge, giving the dot room to travel.

import React, { useEffect, useRef, useState } from "react";

import { COLORS } from "../constants";

const MAP_SIZE = 160;                     // px on screen
const MAP_HALF = MAP_SIZE / 2;
const WORLD_HALF = 76;                    // world-units mapped to MAP_HALF
const SCALE = MAP_HALF / WORLD_HALF;      // world-unit → px ratio

function project(worldX, worldZ) {
  return {
    x: MAP_HALF + worldX * SCALE,
    y: MAP_HALF + worldZ * SCALE,
  };
}

// Static campus icons. `sport` is the arenaApi sport id that clicking the
// pin should switch to; null = the pin is not a sport (Quad / generic
// arena entrance) and won't call setSport.
const ZONES = [
  { id: "quad",       label: "QUAD",       worldX: 0,   worldZ: 0,   color: "#e6b450", r: 6, sport: null },
  { id: "arena",      label: "ARENA",      worldX: 40,  worldZ: 40,  color: "#f7dc95", r: 5, sport: null },
  { id: "basketball", label: "BBALL",      worldX: 45,  worldZ: 20,  color: "#f26a34", r: 4, sport: "basketball" },
  { id: "football",   label: "FTBALL",     worldX: 40,  worldZ: 70,  color: "#7d4dd6", r: 4, sport: "football" },
  { id: "soccer",     label: "SOCCER",     worldX: 70,  worldZ: 40,  color: "#4bb352", r: 4, sport: "soccer" },
  { id: "baseball",   label: "BASEBALL",   worldX: -45, worldZ: 55,  color: "#c8353a", r: 5, sport: "baseball" },
];

export default function LiveMinimap({ arenaApi, isTouch }) {
  const [pos, setPos] = useState({ x: 0, z: 0 });
  const [collapsed, setCollapsed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (collapsed) return undefined;
    // Poll the shared ref every 250ms — cheap DOM update, no per-frame cost.
    timerRef.current = window.setInterval(() => {
      const p = arenaApi?.playerPosRef?.current;
      if (p && typeof p.x === "number") setPos({ x: p.x, z: p.z });
    }, 250);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [arenaApi, collapsed]);

  const player = project(pos.x, pos.z);
  // Clamp inside the map so the dot never disappears if the visitor
  // wanders past our WORLD_HALF window.
  const clamp = (v) => Math.max(6, Math.min(MAP_SIZE - 6, v));
  const px = clamp(player.x);
  const py = clamp(player.y);
  // Show a small arrow at the edge if the dot got clamped, indicating the
  // player is off-map in that direction.
  const clampedOff =
    player.x < 6 || player.x > MAP_SIZE - 6 || player.y < 6 || player.y > MAP_SIZE - 6;

  // Tap-to-teleport handler — mirrors CampusMap: setSport (if the pin is a
  // sport zone) then trigger the shared teleport pose; on touch also flip
  // into walk mode so the joystick is up on arrival.
  const teleportTo = (zone) => {
    if (zone.sport && typeof arenaApi.setSport === "function") {
      arenaApi.setSport(zone.sport);
    }
    if (zone.id === "quad") {
      try {
        if (typeof window.__zone_walker_teleport === "function") {
          window.__zone_walker_teleport(0, 0, 0);
        }
      } catch (e) { /* ignore */ }
    } else {
      const t = arenaApi?.teleportRef?.current?.teleport;
      if (typeof t === "function") {
        // For a sport pin, jump directly to the sport's own centre so the
        // camera lands *on* the field, not at the entrance plaza. For the
        // generic arena pin, default to the plaza (undefined arg).
        if (zone.sport) t([zone.worldX, 0, zone.worldZ]);
        else t();
      }
    }
    if (isTouch) {
      try {
        if (typeof window.__zone_enter_walk === "function") {
          window.__zone_enter_walk();
        }
      } catch (e) { /* ignore */ }
    }
  };

  return (
    <div
      data-testid="live-minimap"
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 44,
        width: collapsed ? 44 : MAP_SIZE + 8,
        transition: "width 220ms ease",
        pointerEvents: "auto",
      }}
    >
      {collapsed ? (
        <button
          data-testid="minimap-expand"
          onClick={() => setCollapsed(false)}
          aria-label="Expand minimap"
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: `1px solid ${COLORS.gold}`,
            background: "linear-gradient(180deg, rgba(11,21,48,0.9) 0%, rgba(6,14,32,0.9) 100%)",
            color: "#f7dc95",
            cursor: "pointer",
            fontSize: 20,
            boxShadow: "0 6px 16px rgba(0,0,0,0.45)",
          }}
        >
          ⟀
        </button>
      ) : (
        <div
          style={{
            padding: 6,
            borderRadius: 12,
            background:
              "linear-gradient(180deg, rgba(11,21,48,0.9) 0%, rgba(6,14,32,0.9) 100%)",
            border: `1px solid ${COLORS.gold}`,
            boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
            backdropFilter: "blur(6px)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 4px 4px",
              color: "#c9b382",
              fontSize: 9,
              letterSpacing: 1.6,
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            <span>Campus Map</span>
            <button
              data-testid="minimap-collapse"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse minimap"
              style={{
                background: "transparent",
                border: "none",
                color: "#c9b382",
                cursor: "pointer",
                fontSize: 12,
                lineHeight: 1,
                padding: 0,
              }}
            >
              −
            </button>
          </div>

          <svg
            width={MAP_SIZE}
            height={MAP_SIZE}
            viewBox={`0 0 ${MAP_SIZE} ${MAP_SIZE}`}
            style={{
              display: "block",
              borderRadius: 8,
              background: "radial-gradient(circle at 50% 50%, #142046 0%, #060c22 90%)",
            }}
          >
            {/* Cardinal compass labels */}
            <text x={MAP_HALF} y="10" textAnchor="middle" fill="#c9b382" fontSize="7" opacity="0.65">N</text>
            <text x={MAP_HALF} y={MAP_SIZE - 4} textAnchor="middle" fill="#c9b382" fontSize="7" opacity="0.65">S</text>
            <text x="6" y={MAP_HALF + 3} textAnchor="middle" fill="#c9b382" fontSize="7" opacity="0.65">W</text>
            <text x={MAP_SIZE - 6} y={MAP_HALF + 3} textAnchor="middle" fill="#c9b382" fontSize="7" opacity="0.65">E</text>

            {/* Central quad ring for visual context */}
            {(() => {
              const q = project(0, 0);
              return (
                <circle
                  cx={q.x}
                  cy={q.y}
                  r={22 * SCALE}
                  fill="none"
                  stroke="rgba(230,180,80,0.25)"
                  strokeWidth="0.5"
                  strokeDasharray="1.5 1.5"
                />
              );
            })()}

            {/* Path lines from quad to each zone */}
            {ZONES.filter((z) => z.id !== "quad").map((z) => {
              const from = project(0, 0);
              const to = project(z.worldX, z.worldZ);
              return (
                <line
                  key={z.id}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke="rgba(230,180,80,0.28)"
                  strokeWidth="0.6"
                  strokeDasharray="1 1"
                />
              );
            })}

            {/* Zone icons — tap any pin to teleport to that zone */}
            {ZONES.map((z) => {
              const p = project(z.worldX, z.worldZ);
              return (
                <g
                  key={z.id}
                  data-testid={`minimap-pin-${z.id}`}
                  onClick={() => teleportTo(z)}
                  style={{ cursor: "pointer" }}
                >
                  {/* Larger transparent hit-target so mobile taps register */}
                  <circle cx={p.x} cy={p.y} r={z.r + 6} fill="transparent" />
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={z.r}
                    fill={z.color}
                    stroke="#f7dc95"
                    strokeWidth="0.6"
                  />
                  <text
                    x={p.x}
                    y={p.y + z.r + 7}
                    textAnchor="middle"
                    fill="#f3ead4"
                    fontSize="5.5"
                    style={{ fontFamily: "sans-serif", fontWeight: 700, pointerEvents: "none" }}
                  >
                    {z.label}
                  </text>
                </g>
              );
            })}

            {/* YOU ARE HERE dot — pulses via a slow CSS animation */}
            <g data-testid="minimap-you-dot">
              <circle
                cx={px}
                cy={py}
                r="7"
                fill="rgba(255, 220, 120, 0.24)"
              >
                <animate attributeName="r" values="6;10;6" dur="1.6s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.35;0.05;0.35" dur="1.6s" repeatCount="indefinite" />
              </circle>
              <circle
                cx={px}
                cy={py}
                r="3.2"
                fill="#f7dc95"
                stroke="#0b1530"
                strokeWidth="0.8"
              />
              {clampedOff && (
                <text
                  x={px}
                  y={py - 8}
                  textAnchor="middle"
                  fill="#f7dc95"
                  fontSize="6"
                  style={{ fontFamily: "sans-serif", fontWeight: 700 }}
                >
                  ↓
                </text>
              )}
            </g>
          </svg>

          <div
            style={{
              padding: "4px 4px 0",
              color: "#c9b382",
              fontSize: 9,
              letterSpacing: 1.2,
              textAlign: "center",
              opacity: 0.75,
            }}
          >
            YOU ARE HERE
          </div>
        </div>
      )}
    </div>
  );
}
